// src/pages/profile/ProfilePage.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { profileHelper } from '@/stores/useProfilePage';
import {
  LogOut, Settings, User, Briefcase, Building, Shield,
  Mail, Phone, FileText, AtSign, Edit2, X, Check,
  Upload, Loader2, AlertCircle, ChevronRight, Star,
  Camera, BadgeCheck,
} from 'lucide-react';

// ── Missing field indicator ───────────────────────────────────────────────────
const Missing: React.FC<{ label: string }> = ({ label }) => (
  <span className="flex items-center gap-1.5 text-sm text-light-subtle dark:text-dark-subtle italic">
    {label} not set
    <span className="inline-block w-2 h-2 rounded-full bg-red-500 flex-shrink-0" title="Missing" />
  </span>
);

// ── Portal card ───────────────────────────────────────────────────────────────
const PortalButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  description: string;
  accent: string;
  onClick: () => void;
}> = ({ icon, label, description, accent, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-4 p-4 rounded-2xl border border-light-border dark:border-[#1e1e1e] bg-light-card dark:bg-dark-card hover:shadow-medium transition-all group text-left w-full"
  >
    <div
      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: `${accent}18` }}
    >
      <div style={{ color: accent }}>{icon}</div>
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-bold text-light-text dark:text-dark-text">{label}</p>
      <p className="text-xs text-light-subtle dark:text-dark-subtle truncate">{description}</p>
    </div>
    <ChevronRight size={16} className="text-light-subtle dark:text-dark-subtle group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
  </button>
);

// ── Info row ──────────────────────────────────────────────────────────────────
const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value?: string | null;
  missingLabel: string;
}> = ({ icon, label, value, missingLabel }) => (
  <div className="flex items-start gap-3 py-3 border-b border-light-border dark:border-[#1e1e1e] last:border-0">
    <div className="w-8 h-8 rounded-lg bg-primary/8 flex items-center justify-center flex-shrink-0 mt-0.5">
      <div className="text-primary">{icon}</div>
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[11px] font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-0.5">{label}</p>
      {value
        ? <p className="text-sm text-light-text dark:text-dark-text">{value}</p>
        : <Missing label={missingLabel} />
      }
    </div>
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────
export const ProfilePage: React.FC = () => {
  const { user, profile, signOut, refreshProfile, hasRole, canAccessPortal, activeRole, setActiveRole } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isEditing, setIsEditing]     = useState(false);
  const [isLoading, setIsLoading]     = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [success, setSuccess]         = useState<string | null>(null);

  const [formData, setFormData] = useState({
    fullName: '', username: '', phoneNumber: '', bio: '',
  });

  useEffect(() => {
    if (profile) {
      setFormData({
        fullName:    profile.full_name    || '',
        username:    profile.username     || '',
        phoneNumber: profile.phone_number || '',
        bio:         profile.bio          || '',
      });
    }
  }, [profile]);

  if (!user) { navigate('/login'); return null; }

  const handleSignOut = async () => {
    try { await signOut(); navigate('/login'); }
    catch { setError('Failed to sign out'); }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true); setError(null); setSuccess(null);
    try {
      await profileHelper.updateProfile(user.id, {
        fullName:    formData.fullName    || undefined,
        username:    formData.username    || undefined,
        phoneNumber: formData.phoneNumber || undefined,
        bio:         formData.bio,
      });
      await refreshProfile();
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally { setIsLoading(false); }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true); setError(null);
    try {
      await profileHelper.uploadAvatar(user.id, file);
      await refreshProfile();
      setSuccess('Avatar updated!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload avatar');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRoleChange = async (role: any) => {
    try { 
      await setActiveRole(role); 
      setSuccess(`Switched to ${role}`); 
      
      const r = role.toLowerCase();
      if (r.includes('business')) navigate('/business');
      else if (r.includes('landlord')) navigate('/landlord');
      else if (r.includes('staff')) navigate('/staff');
      else if (r.includes('tenant') || r.includes('resident')) navigate('/tenant');
      else if (r.includes('admin')) navigate('/admin');
      else navigate('/');
    }
    catch { setError('Failed to change role'); }
  };

  const displayName  = profile?.full_name || user?.email?.split('@')[0] || 'User';
  const initials     = displayName.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
  const memberSince  = user?.created_at ? new Date(user.created_at).toLocaleDateString('en-KE', { month: 'long', year: 'numeric' }) : null;
  const availableRoles = profile?.roles || ['Guest'];

  // Missing field count for the banner badge
  const missingCount = [
    !profile?.username,
    !profile?.phone_number,
    !profile?.bio,
    !profile?.full_name,
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-16">
      <div className="max-w-5xl mx-auto px-5">

        {/* ── Alerts ── */}
        {error && (
          <div className="mt-4 flex items-center gap-3 p-3 rounded-xl bg-danger/10 border border-danger/20">
            <AlertCircle size={16} className="text-danger flex-shrink-0" />
            <p className="text-sm text-danger flex-1">{error}</p>
            <button onClick={() => setError(null)}><X size={14} className="text-danger" /></button>
          </div>
        )}
        {success && (
          <div className="mt-4 flex items-center gap-3 p-3 rounded-xl bg-success/10 border border-success/20">
            <Check size={16} className="text-success flex-shrink-0" />
            <p className="text-sm text-success flex-1">{success}</p>
            <button onClick={() => setSuccess(null)}><X size={14} className="text-success" /></button>
          </div>
        )}

        {/* ── Hero banner ── */}
        <div className="mt-6 rounded-2xl overflow-hidden relative">
          {/* Gradient background */}
          <div className="h-28 bg-gradient-to-br from-primary via-primary/90 to-[#0EA5E9]/70 dark:from-[#0c0c0c] dark:via-[#111] dark:to-primary/20 relative">
            <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-[#0EA5E9]/20 blur-xl" />
            {/* Edit button top-right */}
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/20 backdrop-blur-sm text-white text-xs font-semibold hover:bg-white/30 transition-colors"
              >
                <Edit2 size={12} /> Edit Profile
              </button>
            )}
          </div>

          {/* Avatar + name row */}
          <div className="bg-light-card dark:bg-dark-card px-5 pb-5">
            <div className="flex items-end gap-4 -mt-10 mb-4">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-20 h-20 rounded-2xl overflow-hidden border-4 border-light-card dark:border-dark-card shadow-medium bg-gradient-to-br from-primary to-[#0EA5E9]">
                  {profile?.avatar_url
                    ? <img src={profile.avatar_url} alt={displayName} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center">
                        <span className="text-white font-black text-xl">{initials}</span>
                      </div>
                  }
                </div>
                {isEditing && (
                  <>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-primary flex items-center justify-center shadow-md hover:opacity-90 transition-opacity"
                    >
                      {isUploading ? <Loader2 size={13} className="animate-spin text-white" /> : <Camera size={13} className="text-white" />}
                    </button>
                  </>
                )}
              </div>

              {/* Name + meta */}
              <div className="flex-1 min-w-0 pt-10">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl font-black text-light-text dark:text-dark-text truncate">{displayName}</h1>
                  {profile?.onboarding_completed && (
                    <span title="Verified">
                      <BadgeCheck size={18} className="text-primary flex-shrink-0" />
                    </span>
                  )}
                  {missingCount > 0 && (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-white bg-red-500 px-2 py-0.5 rounded-full">
                      {missingCount} field{missingCount > 1 ? 's' : ''} missing
                    </span>
                  )}
                </div>
                <p className="text-xs text-light-subtle dark:text-dark-subtle mt-0.5">{user.email}</p>
                {memberSince && (
                  <p className="text-[11px] text-light-subtle dark:text-dark-subtle mt-0.5">Member since {memberSince}</p>
                )}
              </div>
            </div>

            {/* Active role pill + role switcher */}
            {availableRoles.length > 0 && !isEditing && (
              <div className="flex flex-wrap gap-2">
                {availableRoles.filter((r: string) => r !== 'Guest').map((role: string) => (
                  <button
                    key={role}
                    onClick={() => handleRoleChange(role)}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                      role === activeRole
                        ? 'bg-primary text-white shadow-sm'
                        : 'bg-light-bg dark:bg-dark-bg text-light-subtle dark:text-dark-subtle border border-light-border dark:border-[#1e1e1e] hover:border-primary dark:hover:border-primary'
                    }`}
                  >
                    <Star size={10} />
                    {role}
                    {role === activeRole && <Check size={10} />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Two-column layout on wide screens ── */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── Left column: profile details ── */}
          <div className="lg:col-span-2 space-y-5">

            {/* Profile info card */}
            <div className="rounded-2xl bg-light-card dark:bg-dark-card border border-light-border dark:border-[#1e1e1e] overflow-hidden">
              {/* Card header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-light-border dark:border-[#1e1e1e]">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-4 rounded-full bg-primary" />
                  <span className="text-sm font-bold text-light-text dark:text-dark-text">Profile Details</span>
                </div>
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-1 text-xs font-semibold text-primary hover:opacity-75 transition-opacity"
                  >
                    <Edit2 size={12} /> Edit
                  </button>
                )}
              </div>

              {isEditing ? (
                <form onSubmit={handleEditSubmit} className="p-5 space-y-4">
                  {/* Full name */}
                  <div>
                    <label className="block text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-1.5">Full Name</label>
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={e => setFormData({ ...formData, fullName: e.target.value })}
                      placeholder="Your full name"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-[#1e1e1e] text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  {/* Username */}
                  <div>
                    <label className="block text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-1.5">Username</label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle text-sm">@</span>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={e => setFormData({ ...formData, username: e.target.value })}
                        placeholder="username"
                        className="w-full pl-7 pr-3.5 py-2.5 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-[#1e1e1e] text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                  </div>
                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={e => setFormData({ ...formData, phoneNumber: e.target.value })}
                      placeholder="+254 700 000 000"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-[#1e1e1e] text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  {/* Bio */}
                  <div>
                    <label className="block text-xs font-semibold text-light-subtle dark:text-dark-subtle uppercase tracking-wider mb-1.5">Bio</label>
                    <textarea
                      value={formData.bio}
                      onChange={e => setFormData({ ...formData, bio: e.target.value })}
                      placeholder="Tell us about yourself…"
                      rows={3}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-[#1e1e1e] text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                    />
                  </div>
                  {/* Actions */}
                  <div className="flex gap-3 pt-1">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      {isLoading ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                      {isLoading ? 'Saving…' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(false);
                        setFormData({ fullName: profile?.full_name || '', username: profile?.username || '', phoneNumber: profile?.phone_number || '', bio: profile?.bio || '' });
                      }}
                      disabled={isLoading}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-[#1e1e1e] text-sm font-semibold text-light-text dark:text-dark-text hover:opacity-80 transition-opacity"
                    >
                      <X size={15} /> Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="px-5 py-2">
                  <InfoRow icon={<Mail size={14} />}    label="Email"        value={user.email}             missingLabel="Email" />
                  <InfoRow icon={<AtSign size={14} />}  label="Username"     value={profile?.username ? `@${profile.username}` : null} missingLabel="Username" />
                  <InfoRow icon={<Phone size={14} />}   label="Phone"        value={profile?.phone_number}  missingLabel="Phone number" />
                  <InfoRow icon={<FileText size={14} />} label="Bio"         value={profile?.bio}           missingLabel="Bio" />
                </div>
              )}
            </div>

            {/* Account actions */}
            <div className="rounded-2xl bg-light-card dark:bg-dark-card border border-light-border dark:border-[#1e1e1e] overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-light-border dark:border-[#1e1e1e]">
                <div className="w-1 h-4 rounded-full bg-light-subtle dark:bg-dark-subtle" />
                <span className="text-sm font-bold text-light-text dark:text-dark-text">Account</span>
              </div>
              <div className="divide-y divide-light-border dark:divide-[#1e1e1e]">
                <button
                  onClick={() => navigate('/settings')}
                  className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-light-bg dark:hover:bg-dark-bg transition-colors group text-left"
                >
                  <Settings size={17} className="text-light-subtle dark:text-dark-subtle" />
                  <span className="flex-1 text-sm font-medium text-light-text dark:text-dark-text">Settings</span>
                  <ChevronRight size={15} className="text-light-subtle dark:text-dark-subtle group-hover:translate-x-0.5 transition-transform" />
                </button>
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-danger/5 transition-colors group text-left"
                >
                  <LogOut size={17} className="text-danger" />
                  <span className="flex-1 text-sm font-medium text-danger">Sign Out</span>
                  <ChevronRight size={15} className="text-danger group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </div>
          </div>

          {/* ── Right column: portals ── */}
          <div className="space-y-5">

            {/* Portals card */}
            {(canAccessPortal('business') || canAccessPortal('landlord') || canAccessPortal('staff') || canAccessPortal('tenant')) && (
              <div className="rounded-2xl bg-light-card dark:bg-dark-card border border-light-border dark:border-[#1e1e1e] overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-light-border dark:border-[#1e1e1e]">
                  <div className="w-1 h-4 rounded-full bg-primary" />
                  <span className="text-sm font-bold text-light-text dark:text-dark-text">Your Portals</span>
                </div>
                <div className="p-3 space-y-2">
                  {canAccessPortal('business') && (
                    <PortalButton
                      icon={<Briefcase size={20} />}
                      label="Business Portal"
                      description="Manage your shop and services"
                      accent="#4F46E5"
                      onClick={() => navigate('/business')}
                    />
                  )}
                  {canAccessPortal('landlord') && (
                    <PortalButton
                      icon={<Building size={20} />}
                      label="Landlord Portal"
                      description="Manage properties and tenants"
                      accent="#0EA5E9"
                      onClick={() => navigate('/landlord')}
                    />
                  )}
                  {canAccessPortal('staff') && (
                    <PortalButton
                      icon={<Shield size={20} />}
                      label="Staff Portal"
                      description="Assignments and tasks"
                      accent="#10B981"
                      onClick={() => navigate('/staff')}
                    />
                  )}
                  {canAccessPortal('tenant') && (
                    <PortalButton
                      icon={<User size={20} />}
                      label="Tenant Portal"
                      description="Lease, payments and maintenance"
                      accent="#8e44ad"
                      onClick={() => navigate('/tenant')}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Switch Role */}
            {profile?.roles && profile.roles.length > 1 && (
              <div className="rounded-2xl bg-light-card dark:bg-dark-card border border-light-border dark:border-[#1e1e1e] overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-light-border dark:border-[#1e1e1e]">
                  <div className="w-1 h-4 rounded-full bg-primary" />
                  <span className="text-sm font-bold text-light-text dark:text-dark-text">Switch Role</span>
                </div>
                <div className="divide-y divide-light-border dark:divide-[#1e1e1e]">
                  {profile.roles.map((role: string) => {
                    const isActive = profile.active_role === role;
                    return (
                      <button
                        key={role}
                        onClick={() => handleRoleChange(role)}
                        className="w-full flex items-center justify-between p-4 hover:bg-light-bg dark:hover:bg-dark-bg transition-colors text-left"
                      >
                        <span className={`font-medium ${isActive ? 'text-primary' : 'text-light-text dark:text-dark-text'}`}>
                          {role}
                        </span>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isActive ? 'border-primary' : 'border-light-subtle dark:border-dark-subtle'}`}>
                          {isActive && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Profile completeness card */}
            <div className="rounded-2xl bg-light-card dark:bg-dark-card border border-light-border dark:border-[#1e1e1e] overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-light-border dark:border-[#1e1e1e]">
                <div className="w-1 h-4 rounded-full bg-[#0EA5E9]" />
                <span className="text-sm font-bold text-light-text dark:text-dark-text">Profile Completeness</span>
              </div>
              <div className="p-4">
                {/* Progress bar */}
                {(() => {
                  const fields = [
                    { label: 'Full name',    done: !!profile?.full_name },
                    { label: 'Username',     done: !!profile?.username },
                    { label: 'Phone number', done: !!profile?.phone_number },
                    { label: 'Bio',          done: !!profile?.bio },
                    { label: 'Avatar',       done: !!profile?.avatar_url },
                  ];
                  const pct = Math.round((fields.filter(f => f.done).length / fields.length) * 100);
                  return (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-light-subtle dark:text-dark-subtle">{pct}% complete</span>
                        <span className="text-xs font-bold" style={{ color: pct === 100 ? '#10B981' : '#4F46E5' }}>
                          {pct === 100 ? 'Complete!' : `${fields.filter(f => !f.done).length} remaining`}
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-light-bg dark:bg-dark-bg overflow-hidden mb-3">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${pct}%`, backgroundColor: pct === 100 ? '#10B981' : '#4F46E5' }}
                        />
                      </div>
                      <div className="space-y-1.5">
                        {fields.map(f => (
                          <div key={f.label} className="flex items-center gap-2">
                            {f.done
                              ? <Check size={13} className="text-success flex-shrink-0" />
                              : <span className="w-3 h-3 rounded-full border-2 border-light-border dark:border-[#333] flex-shrink-0 relative">
                                  <span className="absolute top-0 right-0 w-1.5 h-1.5 rounded-full bg-red-500 -translate-y-0.5 translate-x-0.5" />
                                </span>
                            }
                            <span className={`text-xs ${f.done ? 'text-light-subtle dark:text-dark-subtle' : 'text-light-text dark:text-dark-text font-medium'}`}>
                              {f.label}
                            </span>
                          </div>
                        ))}
                      </div>
                      {pct < 100 && (
                        <button
                          onClick={() => setIsEditing(true)}
                          className="mt-3 w-full py-2 rounded-xl text-xs font-semibold text-white bg-primary hover:opacity-90 transition-opacity"
                        >
                          Complete Profile
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};
